import { ClofoApiError } from './clofo-api'
import { getLoginUrl, getPreviewTag } from '../lib'

type JsonApiError = {
  detail?: string
  source?: { pointer?: string }
  [key: string]: unknown
}

type ApiErrorResponse = {
  // JSON:API format: { errors: [{ detail, source, ... }] }
  // REST format:     { errors: { field: ["msg"], non_field_errors: [...] } }
  errors?:
    | JsonApiError[]
    | {
        non_field_errors?: string[]
        [key: string]: string[] | undefined
      }
  non_field_errors?: string[]
  error?: string[]
}
interface QueryParams {
  [key: string]: string | number | boolean | undefined
}

// TODO: Implement timeout handling
// const DEFAULT_TIMEOUT_MS = 10000

let redirectOn401 = true
const onUnauthorizedCallbacks: Array<() => void | Promise<void>> = []

/**
 * Refreshes the session's auth cookies; registered by the auth module (which imports this one).
 * A 401 from an expired access token is retried once after a refresh instead of logging the user
 * out. This is what rescues a tab Chrome froze: its refresh timer stopped while frozen, so every
 * query refetched on wake goes out on an expired access token while the refresh token is still
 * valid.
 */
let sessionRefresher: (() => Promise<unknown>) | null = null
let inFlightSessionRefresh: Promise<boolean> | null = null

// Auth endpoints are never retried after a refresh: a 401 from the refresh endpoint itself means
// the session is gone, and a 401 from obtain/login is a credentials error.
const isAuthEndpoint = (url: string) => url.includes('/api/v1/auth/')

/**
 * Certain endpoints reuse 401 for a domain signal (e.g. adobe_auth_expired)
 * which does not signify "clofo session invalid". Skip global unauthorized handling
 * if error detail is included in ALLOWLIST below
 *
 * */
const KNOWN_NON_SESSION_401_DETAILS = new Set(['adobe_auth_expired', 'Invalid login attempt'])

export const http = {
  onUnauthorized(callback: () => void | Promise<void>) {
    onUnauthorizedCallbacks.push(callback)
  },

  get(url: string, params?: QueryParams, dealId?: number) {
    const queryString = params ? `?${buildQueryString(params)}` : ''
    return makeRequest(`${url}${queryString}`, 'GET', undefined, dealId)
  },

  post(url: string, body?: Record<string, unknown>, dealId?: number) {
    return makeRequest(url, 'POST', body, dealId)
  },

  patch(url: string, body?: Record<string, unknown>, dealId?: number) {
    return makeRequest(url, 'PATCH', body, dealId)
  },

  put(url: string, body?: Record<string, unknown>, dealId?: number) {
    return makeRequest(url, 'PUT', body, dealId)
  },

  putFile(url: string, file: File | Blob, contentType?: string) {
    return makeRequest(url, 'PUT', file, undefined, contentType || file.type)
  },

  delete(url: string, dealId?: number) {
    return makeRequest(url, 'DELETE', undefined, dealId)
  },

  setSessionRefresher(refresher: () => Promise<unknown>) {
    sessionRefresher = refresher
  },

  /**
   * Refreshes the session, sharing one in-flight refresh between all callers so a burst of 401s
   * (or a wake-up check racing them) issues a single refresh request. The refresh always reaches
   * the server, never a cached token. Resolves to whether the refresh succeeded.
   */
  refreshSession(): Promise<boolean> {
    if (!sessionRefresher) return Promise.resolve(false)
    inFlightSessionRefresh ??= sessionRefresher()
      .then(
        () => true,
        () => false,
      )
      .finally(() => {
        inFlightSessionRefresh = null
      })
    return inFlightSessionRefresh
  },

  setRedirectOn401(value: boolean) {
    redirectOn401 = value
  },

  redirectToLoginWithNextUrl() {
    const { pathname, search, hash } = window.location
    // The search is kept only in previews: the preview Dealroom build carries its deal and region
    // ids there. The production format deliberately carries just path + hash.
    const currentUrl = getPreviewTag() ? `${pathname}${search}${hash}` : `${pathname}${hash}`
    window.location.href = getLoginUrl(window.btoa(currentUrl))
  },
}

async function makeRequest(
  url: string,
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  body?: Record<string, unknown> | File | Blob,
  dealId?: number,
  contentType?: string,
): Promise<unknown> {
  const isFileUpload = body instanceof File || body instanceof Blob

  const requestOptions: RequestInit = {
    method,
    headers: {
      ...(isFileUpload
        ? contentType && { 'Content-Type': contentType }
        : { 'Content-Type': contentType || 'application/json' }),
      ...(dealId && { 'X-Deal-ID': dealId.toString() }),
    },
    ...(body && {
      body: isFileUpload ? body : JSON.stringify(body),
    }),
    credentials: isFileUpload ? 'omit' : 'include',
    // signal: AbortSignal.timeout(DEFAULT_TIMEOUT_MS),
  }

  const canRefreshSession = !isFileUpload && !isAuthEndpoint(url)

  // Don't send a request on cookies that are mid-refresh; it would only 401 and retry
  if (canRefreshSession && inFlightSessionRefresh) {
    await inFlightSessionRefresh
  }

  let response = await fetch(url, requestOptions)

  if (response.status === 401 && canRefreshSession && (await isExpiredTokenResponse(response))) {
    if (await http.refreshSession()) {
      response = await fetch(url, requestOptions)
    }
  }

  if (response.status === 401) {
    let skipUnauthorizedHandling = false
    try {
      const body = (await response.clone().json()) as { detail?: string } | null
      skipUnauthorizedHandling = !!body?.detail && KNOWN_NON_SESSION_401_DETAILS.has(body.detail)
    } catch {
      // Non-JSON or empty 401 body — fall through to normal handling below.
    }

    if (!skipUnauthorizedHandling) {
      await Promise.all(onUnauthorizedCallbacks.map(callback => callback()))
      if (redirectOn401) {
        http.redirectToLoginWithNextUrl()
        // throw an error if the redirect doesn't happen within 10 seconds
        await new Promise((_, reject) => {
          setTimeout(
            () => reject(new Error('Timed out waiting to redirect on a 401 response')),
            10000,
          )
        })
      }
    }
  }

  // Handle successful responses
  if (response.ok) {
    if (response.status === 204) {
      return null
    }

    if (isFileUpload) {
      return response
    }

    return await response.json()
  }

  // Handle error responses
  let errorData: ApiErrorResponse | null = null
  let errorCode: string = ''

  // extract the error message from the response
  if (response.status === 400 || response.status === 422) {
    errorData = await response.json()
    const errors = errorData?.errors
    if (Array.isArray(errors)) {
      // JSON:API format: { errors: [{ detail: "..." }] }
      errorCode = errors[0]?.detail || ''
    } else if (errors?.non_field_errors) {
      errorCode = errors.non_field_errors[0] || ''
    } else if (errorData?.non_field_errors) {
      errorCode = errorData.non_field_errors[0] || ''
    } else if (errorData?.error) {
      errorCode = errorData.error[0] || ''
    } else if (errors) {
      const firstFieldErrors = Object.values(errors).find(v => Array.isArray(v) && v.length)
      errorCode = Array.isArray(firstFieldErrors) ? firstFieldErrors[0] || '' : ''
    }
  }

  throw new ClofoApiError({
    status: response.status,
    statusText: response.statusText,
    method,
    endpoint: url.substring(url.indexOf('/api/')),
    url,
    errorCode,
    errorData,
  })
}

/**
 * Whether a 401 comes from an expired or invalid access token (simplejwt's `token_not_valid`), as
 * opposed to a domain-specific 401 that a refresh can't fix.
 */
async function isExpiredTokenResponse(response: Response) {
  try {
    const body = (await response.clone().json()) as { code?: string } | null
    return body?.code === 'token_not_valid'
  } catch {
    return false
  }
}

function buildQueryString(params: QueryParams): string {
  const searchParams = new URLSearchParams()
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined) {
      searchParams.append(key, String(value))
    }
  })
  return searchParams.toString()
}
